import { useState } from 'preact/hooks';
import { HEROES, applySaveBuild, enforceSaveBudget, saveFree, type HeroSave, type ItemSlot, type SaveOp } from '@knight/shared';
import * as S from '../app/state';
import { t, tn, td } from '../i18n';
import { Btn, HeroPortrait } from './common';
import { KitPanel, GearPanel } from './Kit';
import { api } from '../net/api';
import { heroesView, localHero, saveLocalHero } from '../app/heroes';
import { playSelectedSlot } from './Home';

/**
 * The hero between realms: this is where rank points are spent and gear is arranged, with room
 * for the whole tree on screen. The same operations exist in the realm, in a narrower panel.
 */
export function HeroBuild() {
  const hv = heroesView();
  const slot = S.buildSlot.value;
  const save = hv.heroes.find((h) => h.slot === slot) ?? null;
  const online = S.conn.value === 'online';
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'kit' | 'gear'>('kit');

  async function run(op: SaveOp) {
    if (busy) return;
    setBusy(true);
    try {
      if (online) {
        S.account.value = await api.heroBuild(slot, op);
      } else {
        const cur = localHero(slot);
        if (!cur) return;
        const next = JSON.parse(JSON.stringify(cur)) as HeroSave;
        const r = applySaveBuild(next, op);
        if (!r.ok) {
          S.showToast(t(r.err ?? 'toast.error'));
          return;
        }
        enforceSaveBudget(next);
        next.updatedAt = Date.now();
        saveLocalHero(next);
      }
    } catch (e) {
      const key = (e as { key?: string }).key;
      S.showToast(key && t(key) !== key ? t(key) : t('toast.error', { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  if (!save) {
    return (
      <div class="screen">
        <div class="card narrow panel col">
          <h2>{t('label.setupTitle')}</h2>
          <div class="muted">{t('label.emptySlot')}</div>
          <Btn onClick={() => (S.screen.value = 'home')}>{t('btn.back')}</Btn>
        </div>
      </div>
    );
  }
  const hero = HEROES[save.heroId];
  const points = saveFree(save);
  return (
    <div class="screen">
      <div class="card wide panel col tall">
        <div class="row">
          <HeroPortrait hero={save.heroId} size={48} />
          <div class="col" style={{ gap: 0 }}>
            <h2 style={{ color: hero.accent }}>{tn('hero', save.heroId)}</h2>
            <span class="small muted">
              {t('label.slot', { n: slot + 1 })} · {t('label.rankShort')} {save.rank} · {t('label.runs')} {save.runs}
            </span>
          </div>
          <div class="grow" />
          {points > 0 && <span class="pts">+{points} {t('label.pts')}</span>}
          <Btn small onClick={() => (S.screen.value = 'vault')}>
            {t('label.vault')}
          </Btn>
          <Btn
            small
            primary
            onClick={() => {
              S.setSlot(slot);
              playSelectedSlot();
            }}
          >
            {t('btn.continueHero')}
          </Btn>
          <Btn small onClick={() => (S.screen.value = 'home')}>
            {t('btn.back')}
          </Btn>
        </div>
        <div class="small muted">{t('label.setupHint')}</div>
        <div class="tabs">
          {(['kit', 'gear'] as const).map((k) => (
            <button class={'btn small' + (tab === k ? ' on' : '')} onClick={() => setTab(k)}>
              {t('label.tab.' + k)}
            </button>
          ))}
        </div>
        {tab === 'kit' && (
          <>
            <div class="small muted">
              <b class="gold">{t(`hero.${save.heroId}.trait`)}</b>: {t(`hero.${save.heroId}.traitDesc`)}
            </div>
            <KitPanel
              kit={{
                heroId: save.heroId,
                rank: save.rank,
                points,
                actives: save.actives,
                autos: save.autos,
                traits: save.traits,
                readonly: busy,
                onOp: (op) => void run(op),
              }}
              fame={online ? S.account.value?.fame : undefined}
            />
          </>
        )}
        {tab === 'gear' && (
          <>
            <div class="small muted">{td('hero', save.heroId)}</div>
            <GearPanel
              gear={{
                heroId: save.heroId,
                weapon: save.equipment.weapon,
                armor: save.equipment.armor,
                charm: save.equipment.charm,
                backpack: save.backpack,
                readonly: busy,
                onEquip: (index) => void run({ op: 'equip', index }),
                onUnequip: (slot: ItemSlot) => void run({ op: 'unequip', slot }),
              }}
            />
            <div class="small muted">{t('label.vaultFromSetup')}</div>
          </>
        )}
      </div>
    </div>
  );
}
